import React, { useEffect, useState } from 'react';
import { Box, BoxProps, Typography } from '@mui/material';
import { getImageStorageSettings } from '../../services/imageStorageService';
import { ImageProtectionType } from '../../utils/types';

// أنواع مواضع العلامة المائية
type WatermarkPosition = 'top-left' | 'center';

interface ProtectedImageProps extends Omit<BoxProps, 'component'> {
  src: string;
  alt: string;
  watermarkText?: string;
  forceProtection?: ImageProtectionType; // تجاوز إعدادات النظام واستخدام هذه الحماية
  watermarkPosition?: WatermarkPosition; // موضع العلامة المائية
}

const ProtectedImage: React.FC<ProtectedImageProps> = ({
  src,
  alt,
  watermarkText,
  forceProtection,
  watermarkPosition = 'center', // استخدام المنتصف كوضع افتراضي
  sx,
  ...otherProps
}) => {
  const [imageProtection, setImageProtection] = useState<ImageProtectionType>(ImageProtectionType.NONE);
  const [watermark, setWatermark] = useState<string>('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // إذا كان هناك تجاوز للحماية، استخدمه
        if (forceProtection) {
          setImageProtection(forceProtection);
          return;
        }

        // في حالة عدم وجود تجاوز، استخدم إعدادات النظام
        const settings = await getImageStorageSettings();
        setImageProtection(settings.imageProtection || ImageProtectionType.NONE);
        setWatermark(settings.watermarkText || '');
      } catch (error) {
        console.error('Error loading image protection settings:', error);
        setImageProtection(ImageProtectionType.NONE);
      }
    };

    loadSettings();
  }, [forceProtection]);

  // تحديد خصائص الحماية بناءً على نوع الحماية
  const getProtectionStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {};
    
    switch (imageProtection) {
      case ImageProtectionType.PREVENT_SAVE:
      case ImageProtectionType.FULL:
        // منع النسخ والنقر بزر الماوس الأيمن وإمكانية السحب
        baseStyles.userSelect = 'none';
        baseStyles.WebkitUserSelect = 'none';
        baseStyles.MozUserSelect = 'none';
        baseStyles.pointerEvents = 'none';
        break;
      case ImageProtectionType.PREVENT_OPEN:
        // منع الفتح في صفحة مستقلة فقط (يمكن النسخ)
        baseStyles.pointerEvents = 'none';
        break;
      default:
        // بدون حماية
        break;
    }

    return baseStyles;
  };

  // معالجة حدث النقر بزر الماوس الأيمن
  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (imageProtection === ImageProtectionType.PREVENT_SAVE || 
        imageProtection === ImageProtectionType.FULL) {
      e.preventDefault();
    }
  };

  // معالجة حدث السحب
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (imageProtection === ImageProtectionType.PREVENT_SAVE || 
        imageProtection === ImageProtectionType.FULL) {
      e.preventDefault();
    }
  };

  const actualWatermarkText = watermarkText || watermark;

  // الحصول على نمط العلامة المائية بناءً على موضعها
  const getWatermarkStyle = () => {
    const baseStyle = {
      color: 'rgba(255, 255, 255, 0.85)',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      padding: '4px 12px',
      borderRadius: 1,
      fontSize: '1rem',
      fontWeight: 'bold',
      transform: 'rotate(-15deg)',
      pointerEvents: 'none' as const,
      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
      boxShadow: '0 0 5px rgba(0,0,0,0.3)',
      position: 'absolute' as const,
    };

    // تعديل الموضع حسب الخيار المحدد
    if (watermarkPosition === 'center') {
      return {
        ...baseStyle,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(-15deg)',
        fontSize: '1.2rem',
        padding: '8px 16px',
      };
    } else {
      // top-left position
      return {
        ...baseStyle,
        top: '10px',
        left: '10px',
      };
    }
  };

  return (
    <Box
      position="relative"
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      {...otherProps}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          ...sx,
          maxWidth: '100%',
          height: 'auto',
        }}
        style={getProtectionStyles()}
      />

      {/* إضافة علامة مائية إذا كانت متاحة وتم تمكين الحماية */}
      {actualWatermarkText && (imageProtection !== ImageProtectionType.NONE) && (
        <Typography
          sx={getWatermarkStyle()}
        >
          {actualWatermarkText}
        </Typography>
      )}
    </Box>
  );
};

export default ProtectedImage; 